import { useState, type FormEvent } from 'react';
import { CalendarRange, Download, FileCode2, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../Components/data/PageHeader';
import { Field } from '../Components/forms/Field';
import { Button } from '../Components/ui/Button';
import { Card, CardHead } from '../Components/ui/Card';
import { Input } from '../Components/ui/Input';
import NexusLayout from '../Layouts/NexusLayout';
import api from '../services/api';

function currentMonthRange() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const finalDay = new Date(year, now.getMonth() + 1, 0).getDate();
    return {
        initial: `${year}-${month}-01`,
        final: `${year}-${month}-${String(finalDay).padStart(2, '0')}`,
    };
}

export default function ExportarXml() {
    const initialRange = currentMonthRange();
    const [initialDate, setInitialDate] = useState(initialRange.initial);
    const [finalDate, setFinalDate] = useState(initialRange.final);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

    async function generateXml(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            await api.post('/exportacoes-xml/gerar', {
                data_inicial: initialDate,
                data_final: finalDate,
            });
            setMessage({
                tone: 'success',
                text: 'Arquivo XML gerado. Consulte Relatórios Enviados para baixar ou transmitir.',
            });
        } catch (requestError) {
            console.error('Erro ao gerar XML:', requestError);
            setMessage({ tone: 'error', text: 'Não foi possível gerar o XML para o período informado.' });
        } finally {
            setLoading(false);
        }
    }

    return (
        <NexusLayout title="Exportar XML">
            <div className="nx-page">
                <PageHeader
                    icon={<FileCode2 size={21} strokeWidth={2} />}
                    title="Exportar XML SNGPC"
                    description="Gere o arquivo de movimentação para transmissão à ANVISA."
                />

                <div className="nx-sngpc-form-layout">
                    <Card>
                        <CardHead title="Período da movimentação" />
                        <form className="nx-sngpc-form" onSubmit={generateXml}>
                            <div className="nx-sngpc-notice">
                                <ShieldCheck size={19} />
                                <div>
                                    <strong>Validação do período</strong>
                                    <p>Confira se todas as entradas, dispensações e receitas foram lançadas antes da geração.</p>
                                </div>
                            </div>

                            <div className="nx-form-row nx-form-row--2">
                                <Field label="Data inicial *" htmlFor="xml-data-inicial">
                                    <Input
                                        type="date"
                                        value={initialDate}
                                        max={finalDate}
                                        onChange={(event) => setInitialDate(event.target.value)}
                                        required
                                    />
                                </Field>
                                <Field label="Data final *" htmlFor="xml-data-final">
                                    <Input
                                        type="date"
                                        value={finalDate}
                                        min={initialDate}
                                        onChange={(event) => setFinalDate(event.target.value)}
                                        required
                                    />
                                </Field>
                            </div>

                            {message ? (
                                <div className={`nx-sngpc-message is-${message.tone}`} role="status">
                                    {message.text}
                                </div>
                            ) : null}

                            <div className="nx-sngpc-form-actions">
                                <Button type="submit" loading={loading} loadingText="Gerando XML...">
                                    <Download size={16} />
                                    Gerar arquivo XML
                                </Button>
                            </div>
                        </form>
                    </Card>

                    <Card className="nx-sngpc-side-summary">
                        <CardHead title="Resumo" />
                        <div className="nx-sngpc-period-summary">
                            <CalendarRange size={22} />
                            <span>Período selecionado</span>
                            <strong>
                                {new Date(`${initialDate}T12:00:00`).toLocaleDateString('pt-BR')} a{' '}
                                {new Date(`${finalDate}T12:00:00`).toLocaleDateString('pt-BR')}
                            </strong>
                        </div>
                        <p className="nx-sngpc-side-note">
                            O histórico, o download e a situação de cada arquivo ficam disponíveis em Relatórios Enviados.
                        </p>
                    </Card>
                </div>
            </div>
        </NexusLayout>
    );
}
